import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { AxiosError } from 'axios';
import posthogLib from 'posthog-js';

import httpInstances from '@/api/axiosInstance';
import { clearStoragesFromTokensAndUserInfo } from '@/services/storage';
import { detectEnvironment } from '@/utils/detectEnvironment';

export type AuthApiState = {
  status: 'idle' | 'loading' | 'failed' | 'fulfilled' | 'notFound' | 'onSuppressionList';
  error: string | null;
};

type LoginApiErrorResponse = {
  errors: string[];
};

type ErrorResponse = {
  message: string;
};

const initialState: AuthApiState = {
  status: 'idle',
  error: null
};

export const loginGX = createAsyncThunk(
  'loginGX',
  async (request: { email: string; captchaToken: string }, { rejectWithValue }) => {
    try {
      await httpInstances.axiosInternalInstance.post(
        `${detectEnvironment() === 'staging' ? '/staging/' : '/'}login`,
        { email: request.email },
        { headers: { captchaToken: request.captchaToken } }
      );
    } catch (error) {
      if (error instanceof AxiosError && error.response) {
        return rejectWithValue({ data: error.response.data, status: error.response.status });
      }
      posthogLib.capture('user_login_error', {
        error
      });
      throw error;
    }
  }
);

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    resetStatus(state) {
      state.status = 'idle';
    },
    logout() {
      clearStoragesFromTokensAndUserInfo();
      posthogLib.reset();
      return initialState;
    }
  },
  extraReducers: builder => {
    builder
      .addCase(loginGX.pending, state => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(loginGX.fulfilled, state => {
        state.status = 'fulfilled';
      })
      .addCase(loginGX.rejected, (state, action) => {
        const response = action.payload as { data: unknown; status: number };
        console.error(action.payload);

        if (response?.status === 404) {
          state.status = 'notFound';
        } else if (
          response?.status === 403 &&
          (response?.data as LoginApiErrorResponse)?.errors?.some(error =>
            error.includes('is on suppression list')
          )
        ) {
          state.status = 'onSuppressionList';
        } else {
          console.error(action.payload);
          posthogLib.capture('user_login_error', {
            payload: action.payload
          });
          state.status = 'failed';
          if (action.payload && (action.payload as ErrorResponse).message) {
            state.error = (action.payload as ErrorResponse).message;
          } else {
            state.error = action.error.message ?? 'Login failed';
          }
        }
      });
  }
});

export const { resetStatus, logout } = authSlice.actions;

export default authSlice.reducer;
